import React from 'https://esm.sh/react@19.2.0';
import { createRoot } from 'https://esm.sh/react-dom@19.2.0/client';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Stack,
  ThemeProvider,
  Typography,
  createTheme,
} from 'https://esm.sh/@mui/material@9.0.1?external=react,react-dom';

const locations = [
  {
    city: 'Niagara Falls',
    label: 'Flagship clinic',
    address: '4025 Dorchester Rd, Niagara Falls, ON L2E 6N1',
    phone: '(905) 354-5511',
    phoneHref: 'tel:9053545511',
    page: '/pages/spectacle-clinic-niagara-falls-on',
    directions: 'https://www.google.com/maps/dir/?api=1&destination=4025+Dorchester+Road+Niagara+Falls+ON',
    image: 'https://spectacle.clinic/cdn/shop/files/nf_map_c53a9fb2-a1cb-4e49-aff1-d409fe8e1880_740x.jpg?v=1726400656',
  },
  {
    city: 'St. Catharines',
    label: 'West St. Catharines',
    address: '295 Fourth Ave, St. Catharines, ON L2S 0E7',
    phone: '(905) 684-5511',
    phoneHref: 'tel:9056845511',
    page: '/pages/spectacle-clinic-st-catharines-on',
    directions: 'https://www.google.com/maps/dir/?api=1&destination=295+Fourth+Ave+St+Catharines+ON',
    image: 'https://spectacle.clinic/cdn/shop/files/stc_map_740x.jpg?v=1726397793',
  },
  {
    city: 'Niagara-on-the-Lake',
    label: 'Virgil / NOTL',
    address: '1630 Niagara Stone Rd, Niagara-on-the-Lake, ON L0S 1J0',
    phone: '(905) 468-5555',
    phoneHref: 'tel:9054685555',
    page: '/pages/spectacle-clinic-niagara-on-the-lake-virgil',
    directions: 'https://www.google.com/maps/dir/?api=1&destination=1630+Niagara+Stone+Rd+Niagara-on-the-Lake+ON',
    image: 'https://spectacle.clinic/cdn/shop/files/notl_map_740x.jpg?v=1726400617',
  },
  {
    city: 'Welland',
    label: 'Welland / Fonthill',
    address: '70 Primeway Drive, Welland, ON L3B 0A1',
    phone: '(905) 735-5511',
    phoneHref: 'tel:9057355511',
    page: '/pages/spectacle-clinic-welland',
    directions: 'https://www.google.com/maps/dir/?api=1&destination=70+Primeway+Drive+Welland+ON',
    image: 'https://spectacle.clinic/cdn/shop/files/welland_map_740x.jpg?v=1726397824',
  },
];

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#0DA2E7', dark: '#1991E2', contrastText: '#fff' },
    text: { primary: '#1d1d1f', secondary: '#6e6e73' },
    background: { default: '#f5f5f7', paper: '#fff' },
    divider: '#e5e5ea',
  },
  shape: { borderRadius: 18 },
  typography: {
    fontFamily: 'SF Pro Text, SF Pro Display, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
    h1: { fontSize: 'clamp(30px, 4.2vw, 52px)', fontWeight: 600, letterSpacing: '-0.04em', lineHeight: 1.06 },
    h2: { fontSize: 'clamp(20px, 1.8vw, 26px)', fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.12 },
    body1: { fontSize: 17, letterSpacing: '-0.022em', lineHeight: 1.45 },
    body2: { fontSize: 14, letterSpacing: '-0.018em', lineHeight: 1.42 },
    button: { textTransform: 'none', fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          boxShadow: 'none',
          minHeight: 42,
          '&:hover, &:focus-visible': { boxShadow: 'none' },
          '&.MuiButton-contained, &.MuiButton-contained:hover, &.MuiButton-contained:focus-visible': { color: '#fff !important' },
          '&.MuiButton-outlined, &.MuiButton-outlined:hover, &.MuiButton-outlined:focus-visible': { color: '#1d1d1f !important' },
          '&.MuiButton-text, &.MuiButton-text:hover, &.MuiButton-text:focus-visible': { color: '#0B85C8 !important' },
        },
      },
    },
    MuiCard: { styleOverrides: { root: { boxShadow: 'none' } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
  },
});

function LocationCard({ location }) {
  return React.createElement(Card, {
    variant: 'outlined',
    sx: {
      display: 'flex',
      flexDirection: { xs: 'row', md: 'column' },
      height: '100%',
      borderColor: 'divider',
      bgcolor: 'rgba(255,255,255,.94)',
      overflow: 'hidden',
      transition: 'border-color .2s ease, transform .2s ease',
      '&:hover': { borderColor: 'primary.main', transform: { md: 'translateY(-2px)' } },
    },
  },
    React.createElement(CardActionArea, {
      component: 'a',
      href: location.page,
      'aria-label': `Select ${location.city}`,
      sx: { width: { xs: 132, sm: 168, md: '100%' }, flexShrink: 0, alignSelf: 'stretch' },
    },
      React.createElement(Box, { sx: { position: 'relative', height: '100%', minHeight: { xs: 184, sm: 168, md: 0 }, aspectRatio: { md: '1.42 / 1' }, bgcolor: '#f5f5f7' } },
        React.createElement(CardMedia, {
          component: 'img',
          image: location.image,
          alt: `Map for Spectacle Clinic ${location.city}`,
          loading: location.city === 'Niagara Falls' || location.city === 'St. Catharines' ? 'eager' : 'lazy',
          sx: { width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: 'saturate(.94) contrast(1.02)' },
        }),
        React.createElement(Chip, {
          label: location.label,
          size: 'small',
          sx: {
            position: 'absolute', left: 10, right: 10, bottom: 10,
            bgcolor: 'rgba(255,255,255,.88)', backdropFilter: 'blur(10px)', color: 'text.primary',
            maxWidth: 'calc(100% - 20px)', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
          },
        })
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 } },
      React.createElement(CardContent, { sx: { p: { xs: 1.5, sm: 2, md: 2.25 }, pb: { xs: 1, md: 1.25 }, flex: 1 } },
        React.createElement(Typography, { variant: 'h2', component: 'h2', sx: { mb: .75 } },
          React.createElement(Box, { component: 'a', href: location.page, sx: { color: 'text.primary', textDecoration: 'none', '&:hover': { color: 'primary.main' } } }, location.city)
        ),
        React.createElement(Typography, { variant: 'body2', color: 'text.secondary', sx: { minHeight: { md: 40 } } }, location.address)
      ),
      React.createElement(CardActions, { sx: { p: { xs: 1.5, sm: 2, md: 2.25 }, pt: 0, display: 'grid', gridTemplateColumns: '1fr', gap: 1 } },
        React.createElement(Button, { component: 'a', href: location.page, variant: 'contained', color: 'primary', fullWidth: true, sx: { py: 1.05, color: '#fff !important', bgcolor: '#0DA2E7', '&:hover, &:focus-visible': { color: '#fff !important', bgcolor: '#1991E2' } } }, 'Select this clinic'),
        React.createElement(Stack, { direction: 'row', spacing: 1 },
          React.createElement(Button, { component: 'a', href: location.phoneHref, variant: 'outlined', fullWidth: true, 'aria-label': `Call Spectacle Clinic ${location.city} at ${location.phone}`, sx: { borderColor: 'divider', color: '#1d1d1f !important', bgcolor: '#fff', minWidth: 0, '&:hover, &:focus-visible': { color: '#1d1d1f !important', borderColor: '#d2d2d7', bgcolor: '#f8f8fb' } } },
            React.createElement(Box, { component: 'span', sx: { display: { xs: 'none', sm: 'inline' }, whiteSpace: 'nowrap' } }, location.phone),
            React.createElement(Box, { component: 'span', sx: { display: { xs: 'inline', sm: 'none' }, whiteSpace: 'nowrap' } }, 'Call')
          ),
          React.createElement(Button, { component: 'a', href: location.directions, target: '_blank', rel: 'noopener', variant: 'text', fullWidth: true, sx: { color: '#0B85C8 !important', '&:hover, &:focus-visible': { color: '#0068A8 !important', bgcolor: 'rgba(13,162,231,.08)' } } }, 'Directions')
        )
      )
    )
  );
}

function StoresPage() {
  return React.createElement(ThemeProvider, { theme },
    React.createElement(Box, { sx: { bgcolor: 'background.default', color: 'text.primary', py: { xs: 2.25, md: 5.25 }, px: { xs: 1.75, sm: 3 } } },
      React.createElement(Container, { maxWidth: 'xl', disableGutters: true },
        React.createElement(Box, {
          sx: {
            mb: { xs: 1.75, md: 3.25 },
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: { xs: '14px', md: '18px' },
            bgcolor: '#ffffff',
            border: '1px solid #e5e5ea',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
            gap: { xs: 1.75, md: 4 },
            alignItems: 'center',
          },
        },
          React.createElement(Box, { sx: { maxWidth: 760 } },
            React.createElement(Stack, { direction: 'row', spacing: 1, alignItems: 'center', sx: { mb: 1.25, flexWrap: 'wrap' } },
              React.createElement(Chip, { label: 'Choose a Spectacle Clinic location', sx: { bgcolor: '#E8F6FE', color: '#0B85C8', height: 34, '& .MuiChip-label': { fontSize: 15.5, fontWeight: 700, px: 1.5 } } }),
              React.createElement(Typography, { variant: 'body2', sx: { color: 'text.secondary', fontSize: { xs: 14.5, md: 15.5 } } }, '4 Niagara clinics')
            ),
            React.createElement(Typography, { variant: 'h1', component: 'h1', sx: { fontSize: { xs: '34px', sm: '40px', md: '48px' }, letterSpacing: '-0.045em' } }, 'Select your clinic'),
            React.createElement(Typography, { variant: 'body1', color: 'text.secondary', sx: { mt: 1.25, maxWidth: 700, fontSize: { xs: 16, md: 18 }, lineHeight: 1.45 } }, 'Find the closest Spectacle Clinic, then choose details, call, directions, or booking.')
          ),
          React.createElement(Stack, { spacing: 1.25, sx: { minWidth: { md: 220 } } },
            React.createElement(Button, { component: 'a', href: 'https://spectacle.clinic/pages/appointments/', variant: 'contained', size: 'large', sx: { px: 3, py: 1.35, fontSize: 16, fontWeight: 700, color: '#fff !important', bgcolor: '#0DA2E7', '&:hover, &:focus-visible': { color: '#fff !important', bgcolor: '#1991E2' } } }, 'Book online')
          )
        ),
        React.createElement(Box, {
          role: 'list',
          'aria-label': 'Spectacle Clinic locations',
          sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: { xs: 1.5, sm: 2, lg: 2.25 } },
        }, locations.map((location) => React.createElement(Box, { role: 'listitem', key: location.city, sx: { minWidth: 0 } }, React.createElement(LocationCard, { location }))))
      )
    )
  );
}

const rootEl = document.getElementById('sc-stores-mui-root');
if (rootEl) {
  createRoot(rootEl).render(React.createElement(StoresPage));
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      rootEl.dataset.enhanced = 'true';
      document.getElementById('sc-stores-fallback')?.remove();
    });
  });
}
